import React, { useEffect, useState, useRef, memo } from 'react';
import { Lock, MapPin, Save, CheckCircle, AlertCircle, Plus, Image as ImageIcon, ArrowLeft } from 'lucide-react';
interface Plan {
  id: string;
  name: string;
  imageUrl: string;
}
interface Pin {
  x: number;
  y: number;
}
interface PendingPin {
  id: string;
  telefono: string;
  image_url: string;
  proyecto: string;
  plano: string;
  x: number | null;
  y: number | null;
}
export function PlanoTool() {
  // Auth State
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState(false);
  // App State
  const [pendingPins, setPendingPins] = useState<PendingPin[]>([]);
  const [selectedPin, setSelectedPin] = useState<PendingPin | null>(null);
  const [isLoadingPins, setIsLoadingPins] = useState(false);
  const [projectNameInput, setProjectNameInput] = useState('');
  const [projectName, setProjectName] = useState('');
  const [plans, setPlans] = useState<Plan[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<string>('');
  const [pin, setPin] = useState<Pin | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [saveErrorMsg, setSaveErrorMsg] = useState('');
  const [isLoadingPlanos, setIsLoadingPlanos] = useState(false);
  // Read telefono from URL params
  const [telefono, setTelefono] = useState<string>('');
  useEffect(() => {
    const url = new URL(window.location.href);
    const tel = url.searchParams.get('telefono');
    if (tel) setTelefono(tel);
  }, []);
  // Fetch pending pins when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchPendingPins();
    }
  }, [isAuthenticated]);
  // Fetch planos when authenticated and telefono is available
  useEffect(() => {
    if (isAuthenticated && telefono) {
      fetchPlanos();
    }
  }, [isAuthenticated, telefono]);
  const fetchPlanos = async () => {
    setIsLoadingPlanos(true);
    try {
      const response = await fetch('https://hook.us2.make.com/REEMPLAZAR_POR_TU_WEBHOOK', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          telefono: telefono
        })
      });
      const data = await response.json();
      const mappedPlans = data.map((item: any) => ({
        id: item.id,
        name: item.nombre,
        imageUrl: item.image_url
      }));
      setPlans(mappedPlans);
      if (mappedPlans.length > 0) {
        setSelectedPlanId(mappedPlans[0].id);
      }
    } catch (error) {
      console.error('Error loading planos', error);
    } finally {
      setIsLoadingPlanos(false);
    }
  };
  const fetchPendingPins = async () => {
    setIsLoadingPins(true);
    try {
      // Placeholder webhook URL for fetching
      const response = await fetch('https://hook.us2.make.com/XXXXXXXX');
      if (response.ok) {
        const data = await response.json();
        setPendingPins(data);
      } else {
        console.error('Failed to fetch pending pins');
      }
    } catch (error) {
      console.error('Error fetching pending pins:', error);
    } finally {
      setIsLoadingPins(false);
    }
  };
  // Cleanup object URLs to prevent memory leaks
  useEffect(() => {
    return () => {
      plans.forEach(plan => URL.revokeObjectURL(plan.imageUrl));
    };
  }, [plans]);
  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === 'riezko') {
      setIsAuthenticated(true);
      setAuthError(false);
    } else {
      setAuthError(true);
    }
  };
  const handleCreateProject = () => {
    if (projectNameInput.trim()) {
      setProjectName(projectNameInput.trim());
    }
  };
  const handleImageClick = (e: React.MouseEvent<HTMLImageElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    setPin({
      x,
      y
    });
    setSaveStatus('idle');
    setSaveErrorMsg('');
  };
  const handleSavePin = async () => {
    setSaveStatus('idle');
    setSaveErrorMsg('');
    const selectedPlan = plans.find(p => p.id === selectedPlanId);
    if (!projectName || !selectedPlan || !pin || !selectedPin) {
      setSaveStatus('error');
      setSaveErrorMsg('Faltan campos requeridos (Proyecto, Plano o Pin)');
      return;
    }
    setIsSaving(true);
    const payload = {
      record_id: selectedPin.id,
      proyecto: projectName,
      plano: selectedPlan.name,
      x: Number(pin.x),
      y: Number(pin.y),
      estado: 'completo'
    };
    try {
      const response = await fetch('https://hook.us2.make.com/dgr6qsvlp5htc1tp31xge9qe0brbx3o9', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const data = await response.json().catch(() => null);
      console.log('Webhook response:', data);
      if (response.ok && data?.ok === true) {
        setSaveStatus('success');
        setPin(null);
        // Remove from list and go back
        setPendingPins(prev => prev.filter(p => p.id !== selectedPin.id));
        setTimeout(() => {
          setSelectedPin(null);
          setSaveStatus('idle');
        }, 1500);
      } else {
        setSaveStatus('error');
        setSaveErrorMsg('Error en servidor');
      }
    } catch (error) {
      console.error('Error guardando pin:', error);
      setSaveStatus('error');
      setSaveErrorMsg('Error de conexión');
    } finally {
      setIsSaving(false);
    }
  };
  if (!isAuthenticated) {
    return <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8" data-id="element-651">
        <div className="sm:mx-auto sm:w-full sm:max-w-md" data-id="element-652">
          <div className="flex justify-center" data-id="element-653">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center" data-id="element-654">
              <Lock className="w-6 h-6 text-blue-600" data-id="element-655" />
            </div>
          </div>
          <h2 className="mt-6 text-center text-2xl font-bold text-gray-900" data-id="element-656">
            Acceso interno
          </h2>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md" data-id="element-657">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10" data-id="element-658">
            <form className="space-y-6" onSubmit={handleLogin} data-id="element-659">
              <div data-id="element-660">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700" data-id="element-661">
                  Clave
                </label>
                <div className="mt-1" data-id="element-662">
                  <input id="password" name="password" type="password" required value={password} onChange={e => setPassword(e.target.value)} className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-663" />
                </div>
                {authError && <p className="mt-2 text-sm text-red-600 flex items-center" data-id="element-664">
                    <AlertCircle className="w-4 h-4 mr-1" data-id="element-665" /> Clave incorrecta
                  </p>}
              </div>

              <div data-id="element-666">
                <button type="submit" className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" data-id="element-667">
                  Entrar
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>;
  }
  const selectedPlan = plans.find(p => p.id === selectedPlanId);
  return <div className="min-h-screen bg-gray-50 pb-20" data-id="element-668">
      <header className="bg-white shadow-sm" data-id="element-669">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between" data-id="element-670">
          <div className="flex items-center" data-id="element-671">
            <MapPin className="w-6 h-6 text-blue-600 mr-2" data-id="element-672" />
            <h1 className="text-lg font-bold text-gray-900" data-id="element-673">
              Obra Digital - Planos
            </h1>
          </div>
          {selectedPin && <button onClick={() => {
          setSelectedPin(null);
          setSaveStatus('idle');
          setPin(null);
        }} className="flex items-center text-sm text-gray-600 hover:text-gray-900" data-id="element-674">
              <ArrowLeft className="w-4 h-4 mr-1" data-id="element-675" /> Volver a lista
            </button>}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8" data-id="element-676">
        {!selectedPin /* LIST VIEW */ ? <section className="bg-white p-6 rounded-xl shadow-sm border border-gray-100" data-id="element-677">
            <div className="flex items-center justify-between mb-6" data-id="element-678">
              <h2 className="text-base font-semibold text-gray-900" data-id="element-679">
                Fotos pendientes
              </h2>
              <button onClick={fetchPendingPins} className="text-sm text-blue-600 hover:text-blue-800" data-id="element-680">
                Actualizar
              </button>
            </div>

            {isLoadingPins ? <div className="text-center py-12" data-id="element-681">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" data-id="element-682"></div>
                <p className="mt-2 text-sm text-gray-500" data-id="element-683">Cargando fotos...</p>
              </div> : pendingPins.length === 0 ? <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300" data-id="element-684">
                <CheckCircle className="mx-auto h-12 w-12 text-green-400" data-id="element-685" />
                <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-686">
                  No hay fotos pendientes
                </h3>
                <p className="mt-1 text-sm text-gray-500" data-id="element-687">
                  Todas las fotos han sido ubicadas.
                </p>
              </div> : <div className="space-y-4" data-id="element-688">
                {pendingPins.map(item => <div key={item.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200" data-id="element-689">
                    <div className="flex items-center space-x-4" data-id="element-690">
                      <div className="w-16 h-16 rounded-md overflow-hidden bg-gray-200 flex-shrink-0" data-id="element-691">
                        {item.image_url ? <img src={item.image_url} alt="Foto pendiente" className="w-full h-full object-cover" data-id="element-692" /> : <ImageIcon className="w-8 h-8 m-4 text-gray-400" data-id="element-693" />}
                      </div>
                      <div data-id="element-694">
                        <p className="text-sm font-medium text-gray-900 truncate max-w-[150px] sm:max-w-xs" data-id="element-695">
                          {item.telefono}
                        </p>
                        <p className="text-xs text-gray-500" data-id="element-696">
                          ID: {item.id.substring(0, 8)}...
                        </p>
                      </div>
                    </div>
                    <button onClick={() => setSelectedPin(item)} className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" data-id="element-697">
                      <MapPin className="w-4 h-4 mr-1 sm:mr-2" data-id="element-698" />
                      <span className="hidden sm:inline" data-id="element-699">Ubicar</span>
                    </button>
                  </div>)}
              </div>}
          </section> : <>
            {/* PHOTO PREVIEW */}
            <section className="bg-white p-6 rounded-xl shadow-sm border border-gray-100" data-id="element-700">
              <h2 className="text-base font-semibold text-gray-900 mb-4" data-id="element-701">
                Foto a ubicar
              </h2>
              <div className="flex flex-col sm:flex-row gap-6 items-start" data-id="element-702">
                <div className="w-full sm:w-48 h-48 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0 border border-gray-200" data-id="element-703">
                  {selectedPin.image_url ? <img src={selectedPin.image_url} alt="Foto" className="w-full h-full object-cover" data-id="element-704" /> : <div className="w-full h-full flex items-center justify-center" data-id="element-705">
                      <ImageIcon className="w-12 h-12 text-gray-300" data-id="element-706" />
                    </div>}
                </div>
                <div className="space-y-2" data-id="element-707">
                  <p className="text-sm text-gray-600" data-id="element-708">
                    <span className="font-medium text-gray-900" data-id="element-709">Teléfono:</span>{' '}
                    {selectedPin.telefono}
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-710">
                    <span className="font-medium text-gray-900" data-id="element-711">ID:</span>{' '}
                    {selectedPin.id}
                  </p>
                </div>
              </div>
            </section>

            {/* SECTION 1 — PROYECTO */}
            <section className="bg-white p-6 rounded-xl shadow-sm border border-gray-100" data-id="element-712">
              <h2 className="text-base font-semibold text-gray-900 mb-4" data-id="element-713">
                1. Proyecto
              </h2>
              {!projectName ? <div className="flex flex-col sm:flex-row gap-3" data-id="element-714">
                  <input type="text" placeholder="Nombre del proyecto" value={projectNameInput} onChange={e => setProjectNameInput(e.target.value)} className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-715" />
                  <button onClick={handleCreateProject} disabled={!projectNameInput.trim()} className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-716">
                    <Plus className="w-4 h-4 mr-2" data-id="element-717" /> Crear proyecto
                  </button>
                </div> : <div className="flex items-center justify-between bg-blue-50 px-4 py-3 rounded-md border border-blue-100" data-id="element-718">
                  <span className="text-sm font-medium text-blue-900" data-id="element-719">
                    Proyecto actual:{' '}
                    <span className="font-bold" data-id="element-720">{projectName}</span>
                  </span>
                  <button onClick={() => setProjectName('')} className="text-xs text-blue-600 hover:text-blue-800 font-medium" data-id="element-721">
                    Cambiar
                  </button>
                </div>}
            </section>

            {/* SECTION 2 — PLANOS */}
            <section className="bg-white p-6 rounded-xl shadow-sm border border-gray-100" data-id="element-722">
              <h2 className="text-base font-semibold text-gray-900 mb-4" data-id="element-723">
                2. Planos
              </h2>

              <div className="space-y-4" data-id="element-724">
                {isLoadingPlanos ? <div className="text-center py-8" data-id="element-725">
                    <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" data-id="element-726"></div>
                    <p className="mt-2 text-sm text-gray-500" data-id="element-727">
                      Cargando planos...
                    </p>
                  </div> : plans.length > 0 ? <div data-id="element-728">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-729">
                      Seleccionar plano
                    </label>
                    <select value={selectedPlanId} onChange={e => {
                setSelectedPlanId(e.target.value);
                setPin(null);
              }} className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md border" data-id="element-730">
                      {plans.map(plan => <option key={plan.id} value={plan.id} data-id="element-731">
                          {plan.name}
                        </option>)}
                    </select>
                  </div> : <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300" data-id="element-732">
                    <ImageIcon className="mx-auto h-10 w-10 text-gray-400" data-id="element-733" />
                    <p className="mt-2 text-sm text-gray-500" data-id="element-734">
                      {telefono ? 'No se encontraron planos para este teléfono.' : 'No se detectó teléfono en la URL.'}
                    </p>
                  </div>}
              </div>
            </section>

            {/* SECTION 3 — INTERACCIÓN CON PLANO */}
            <section className="bg-white p-6 rounded-xl shadow-sm border border-gray-100" data-id="element-735">
              <h2 className="text-base font-semibold text-gray-900 mb-4" data-id="element-736">
                3. Interacción con plano
              </h2>

              {!selectedPlan ? <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300" data-id="element-737">
                  <ImageIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-738" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-739">
                    No hay plano seleccionado
                  </h3>
                  <p className="mt-1 text-sm text-gray-500" data-id="element-740">
                    Selecciona un plano arriba.
                  </p>
                </div> : <div className="space-y-4" data-id="element-741">
                  <p className="text-sm text-gray-600 font-medium flex items-center" data-id="element-742">
                    <MapPin className="w-4 h-4 mr-1 text-blue-600" data-id="element-743" /> Haz click
                    en el plano para ubicar el pin
                  </p>

                  <div className="relative border border-gray-200 rounded-lg overflow-hidden bg-gray-100" data-id="element-744">
                    <img src={selectedPlan.imageUrl} alt={selectedPlan.name} onClick={handleImageClick} className="w-full h-auto block cursor-crosshair" data-id="element-745" />
                    {pin && <div className="absolute w-4 h-4 bg-red-500 rounded-full border-2 border-white shadow-md transform -translate-x-1/2 -translate-y-1/2 pointer-events-none" style={{
                left: `${pin.x * 100}%`,
                top: `${pin.y * 100}%`
              }} data-id="element-746" />}
                  </div>

                  {pin && <div className="flex gap-4 text-sm font-mono bg-gray-50 p-3 rounded-md border border-gray-200" data-id="element-747">
                      <div data-id="element-748">
                        <span className="text-gray-500" data-id="element-749">X:</span>{' '}
                        {pin.x.toFixed(4)}
                      </div>
                      <div data-id="element-750">
                        <span className="text-gray-500" data-id="element-751">Y:</span>{' '}
                        {pin.y.toFixed(4)}
                      </div>
                    </div>}
                </div>}
            </section>

            {/* SECTION 4 — GUARDAR PIN */}
            <section className="bg-white p-6 rounded-xl shadow-sm border border-gray-100" data-id="element-752">
              <h2 className="text-base font-semibold text-gray-900 mb-4" data-id="element-753">
                4. Guardar pin
              </h2>

              <div className="space-y-4" data-id="element-754">
                <button onClick={handleSavePin} disabled={isSaving || saveStatus === 'success' || !pin || !selectedPlan || !projectName} className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-755">
                  {isSaving ? <span className="flex items-center" data-id="element-756">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-757">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-758"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-759"></path>
                      </svg>
                      Guardando...
                    </span> : <span className="flex items-center" data-id="element-760">
                      <Save className="w-4 h-4 mr-2" data-id="element-761" /> Guardar ubicación
                    </span>}
                </button>

                {saveStatus === 'success' && <div className="rounded-md bg-green-50 p-4 border border-green-200" data-id="element-762">
                    <div className="flex" data-id="element-763">
                      <div className="flex-shrink-0" data-id="element-764">
                        <CheckCircle className="h-5 w-5 text-green-400" data-id="element-765" />
                      </div>
                      <div className="ml-3" data-id="element-766">
                        <p className="text-sm font-medium text-green-800" data-id="element-767">
                          Ubicación guardada correctamente. Volviendo a la
                          lista...
                        </p>
                      </div>
                    </div>
                  </div>}

                {saveStatus === 'error' && <div className="rounded-md bg-red-50 p-4 border border-red-200" data-id="element-768">
                    <div className="flex" data-id="element-769">
                      <div className="flex-shrink-0" data-id="element-770">
                        <AlertCircle className="h-5 w-5 text-red-400" data-id="element-771" />
                      </div>
                      <div className="ml-3" data-id="element-772">
                        <p className="text-sm font-medium text-red-800" data-id="element-773">
                          {saveErrorMsg}
                        </p>
                      </div>
                    </div>
                  </div>}
              </div>
            </section>
          </>}
      </main>
    </div>;
}